import { useEffect, useState } from 'react';
import {
  SERVICE_CATEGORY_LABELS, URGENT_SERVICE_CATEGORIES, serviceStatusLabel,
  TERMINAL_SERVICE_STATUSES,
  type ServiceRequestRow, type ServiceRequestDetail, type ServiceRequestStatus,
  type ServiceRegionScopeView, type ServiceTypeRow, type ServiceRequestAnswer,
} from 'shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { usePagedList } from '../hooks/usePagedList';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { Icon } from '../components/icons';

/**
 * How long a request has been waiting, in the words a handler thinks in.
 *
 * Age is the number somebody acts on, so it sits next to the clock time rather than
 * replacing it: "17 mnt lalu" decides what to pick up, "14:02" is what gets said on the
 * phone.
 */
function age(iso: string): string {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'baru saja';
  if (minutes < 60) return `${minutes} mnt lalu`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} jam lalu`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'kemarin' : `${days} hari lalu`;
}

const clock = (iso: string) => new Date(iso).toLocaleTimeString('id-ID', {
  hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta',
});

const day = (iso: string) => new Date(iso).toLocaleDateString('id-ID', {
  day: '2-digit', month: 'short', timeZone: 'Asia/Jakarta',
});

function statusPill(status: ServiceRequestStatus): string {
  if (status === 'submitted') return 'pill accent';
  if (status === 'completed') return 'pill ok';
  if (status === 'assigned' || status === 'in_progress') return 'pill warn';
  return 'pill';
}

/** Answers can be text, a number, a boolean, or a list — each reads differently. */
function answerText(value: ServiceRequestAnswer['value']): string {
  if (value === null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Ya' : 'Tidak';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

/**
 * Permintaan Layanan — the queue.
 *
 * Paged and searched on the SERVER, following the pattern the master-data tables use and
 * deliberately not the `pageSize=100` shortcut the Pesanan page still takes. A branch
 * operator's rows are scoped in the query. A pinned account (branch operator, kota
 * services admin) sees its region as a fixed chip rather than a filter, because a select
 * of six regions where five always return nothing reads as a broken page.
 */
export function ServiceRequests() {
  const { can } = useAuth();
  const handles = can('service_request:handle');

  const [regionId, setRegionId] = useState('');
  const [serviceTypeId, setServiceTypeId] = useState('');
  const [openOnly, setOpenOnly] = useState(true);

  const list = usePagedList<ServiceRequestRow>('/services/requests', {
    params: {
      regionId: regionId || null,
      serviceTypeId: serviceTypeId || null,
      openOnly: openOnly ? 'true' : null,
    },
  });

  const [scope, setScope] = useState<ServiceRegionScopeView | null>(null);
  const [types, setTypes] = useState<ServiceTypeRow[]>([]);
  const [detail, setDetail] = useState<ServiceRequestDetail | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, t] = await Promise.all([
          api.get<ServiceRegionScopeView>('/services/regions/mine'),
          api.get<{ items: ServiceTypeRow[] }>('/services/types?pageSize=100&includeInactive=true'),
        ]);
        if (cancelled) return;
        setScope(r);
        setTypes(t.items);
      } catch {
        // A missing filter list is not worth an error banner: the table still works, it
        // just cannot be narrowed by type or region.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function openDetail(id: string) {
    try {
      setDetail(await api.get<ServiceRequestDetail>(`/services/requests/${id}`));
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Gagal memuat permintaan.');
    }
  }

  const unhandled = list.items.filter((r) => r.status === 'submitted').length;

  // The place name the empty state speaks of: the pinned region, the chosen filter, or
  // nowhere in particular.
  const placeName = scope?.scoped
    ? scope.regions[0]?.name
    : scope?.regions.find((r) => r.id === regionId)?.name;
  const where = placeName ? ` di ${placeName}` : '';

  const emptyState = (
    <div className="empty-state">
      <p>
        {openOnly
          ? `Tidak ada permintaan yang menunggu${where}.`
          : `Belum ada permintaan layanan${where}.`}
      </p>
      {openOnly && (
        <button type="button" onClick={() => setOpenOnly(false)}>
          Tampilkan juga yang sudah selesai
        </button>
      )}
    </div>
  );

  return (
    <div className="stack">
      <div className="page-head">
        <h2>Permintaan Layanan</h2>
        <p>
          {unhandled > 0
            ? `${unhandled} permintaan di halaman ini belum ditangani.`
            : 'Permintaan yang belum selesai tampil lebih dulu, yang paling lama menunggu di atas.'}
        </p>
      </div>

      {notice && <div className="msg error">{notice}</div>}

      <DataTable
        list={list}
        searchPlaceholder="Cari nomor, nama, atau telepon…"
        empty={emptyState}
        emptyFiltered="Tidak ada permintaan yang cocok dengan pencarian."
        columns={[
          'Nomor', 'Jenis', 'Pemohon', 'Masuk', 'Status', { label: '', width: '80px' },
        ]}
        filters={
          <>
            {scope?.scoped ? (
              <span className="scope-chip" title="Akun Anda hanya memegang wilayah ini">
                <Icon name="lock" size={14} />
                <span>Wilayah</span>
                {scope.regions[0]?.name ?? 'belum diatur'}
              </span>
            ) : (
              <select
                value={regionId}
                onChange={(e) => setRegionId(e.target.value)}
                aria-label="Filter wilayah"
              >
                <option value="">Semua wilayah</option>
                {scope?.regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            )}
            <select
              value={serviceTypeId}
              onChange={(e) => setServiceTypeId(e.target.value)}
              aria-label="Filter jenis layanan"
            >
              <option value="">Semua jenis</option>
              {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <label className="check">
              <input
                type="checkbox"
                checked={openOnly}
                onChange={(e) => setOpenOnly(e.target.checked)}
              />
              <span>Hanya yang belum selesai</span>
            </label>
          </>
        }
      >
        {list.items.map((row) => (
          <tr key={row.id}>
            <td>
              <div className="svc-name">
                <span
                  className={
                    TERMINAL_SERVICE_STATUSES.includes(row.status) ? 'urgency-stripe is-done'
                      : URGENT_SERVICE_CATEGORIES.includes(row.category) ? 'urgency-stripe'
                        : 'urgency-stripe is-calm'
                  }
                  aria-hidden="true"
                />
                <span className="mono" style={{ fontSize: 12 }}>{row.number}</span>
              </div>
            </td>
            <td>
              {row.serviceTypeName}
              <div className="muted" style={{ fontSize: 11.5 }}>
                {SERVICE_CATEGORY_LABELS[row.category]} · {row.regionName}
              </div>
            </td>
            <td>
              {row.contactName}
              <div className="mono muted" style={{ fontSize: 11.5 }}>{row.contactPhone}</div>
            </td>
            <td>
              <span className="mono">{clock(row.createdAt)}</span>
              <div className="muted" style={{ fontSize: 11.5 }}>
                {day(row.createdAt)} · {age(row.createdAt)}
              </div>
            </td>
            <td><span className={statusPill(row.status)}>{row.statusLabel}</span></td>
            <td>
              <button type="button" className="ghost" onClick={() => openDetail(row.id)}>
                Buka
              </button>
            </td>
          </tr>
        ))}
      </DataTable>

      <RequestDetail
        detail={detail}
        canHandle={handles}
        onClose={() => setDetail(null)}
        onChanged={(next, message) => {
          setDetail(next);
          setNotice(message);
          list.reload();
        }}
      />
    </div>
  );
}

interface DetailProps {
  detail: ServiceRequestDetail | null;
  canHandle: boolean;
  onClose: () => void;
  onChanged: (next: ServiceRequestDetail, message: string) => void;
}

/**
 * One request, with its answers, its timeline, and the moves that are legal from here.
 *
 * The action buttons come from `allowedTransitions`, which the server derived from the
 * same transition table it enforces. Offering a button the server would refuse is the
 * failure this avoids — the UI and the check cannot disagree, because there is one table.
 */
function RequestDetail({ detail, canHandle, onClose, onChanged }: DetailProps) {
  const [note, setNote] = useState('');
  const [assignmentNote, setAssignmentNote] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [busy, setBusy] = useState<ServiceRequestStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNote('');
    setAssignmentNote(detail?.assignmentNote ?? '');
    setRejectionReason('');
    setError(null);
  }, [detail?.id, detail?.assignmentNote]);

  if (!detail) return null;

  async function move(status: ServiceRequestStatus) {
    if (status === 'rejected' && !rejectionReason.trim()) {
      setError('Tulis alasan penolakan dulu — pemohon akan membacanya.');
      return;
    }
    setBusy(status);
    setError(null);
    try {
      const next = await api.patch<ServiceRequestDetail>(
        `/services/requests/${detail!.id}/status`,
        {
          status,
          note: note.trim() || null,
          assignmentNote: assignmentNote.trim() || null,
          rejectionReason: status === 'rejected' ? rejectionReason.trim() : null,
        },
      );
      onChanged(next, `${detail!.number} → ${next.statusLabel}.`);
      setNote('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gagal mengubah status.');
    } finally {
      setBusy(null);
    }
  }

  const canReject = detail.allowedTransitions.includes('rejected');

  return (
    <Modal
      open
      size="wide"
      title={detail.number}
      description={
        `${detail.serviceTypeName} · ${detail.regionName} · masuk `
        + `${day(detail.createdAt)} ${clock(detail.createdAt)}`
      }
      onClose={onClose}
      footer={<button type="button" className="ghost" onClick={onClose}>Tutup</button>}
    >
      <div className="stack" style={{ gap: '1rem' }}>
        {error && <div className="msg error">{error}</div>}

        <div className="svc-detail">
          <div className="stack" style={{ gap: '1rem' }}>
            <section className="card" style={{ padding: '0.9rem 1rem' }}>
              <span className="label">Pemohon</span>
              <dl className="kv">
                <dt>Nama kontak</dt><dd>{detail.contactName}</dd>
                <dt>Telepon</dt>
                <dd><a href={`tel:${detail.contactPhone}`} className="mono">{detail.contactPhone}</a></dd>
                <dt>Akun AiYO</dt>
                <dd>
                  {detail.requesterName}
                  <div className="mono muted" style={{ fontSize: 11 }}>
                    {detail.requesterExternalUserId}
                  </div>
                </dd>
                <dt>Permintaan sebelumnya</dt>
                <dd>{detail.requesterRequestCount - 1}</dd>
              </dl>
            </section>

            <section className="card" style={{ padding: '0.9rem 1rem' }}>
              <span className="label">
                Jawaban formulir
                {detail.versionNo !== null && ` · versi ${detail.versionNo}`}
              </span>
              {detail.answers.length === 0 ? (
                <p className="muted" style={{ marginTop: '0.5rem' }}>
                  Tidak ada jawaban yang tercatat.
                </p>
              ) : (
                <dl className="answers">
                  {detail.answers.map((a) => (
                    <div key={a.key}>
                      <dt>{a.label}</dt>
                      <dd>{answerText(a.value)}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {detail.versionNo !== null && (
                <p className="muted" style={{ fontSize: 11.5, marginTop: '0.6rem' }}>
                  Pertanyaan ditampilkan sesuai versi yang benar-benar diisi pemohon, bukan
                  versi terbaru.
                </p>
              )}
            </section>

            {detail.rejectionReason && (
              <div className="msg error">
                <strong>Alasan penolakan:</strong> {detail.rejectionReason}
              </div>
            )}
          </div>

          <div className="stack" style={{ gap: '1rem' }}>
            <section className="card" style={{ padding: '0.9rem 1rem' }}>
              <span className="label">Riwayat status</span>
              <ol className="timeline">
                {detail.timeline.map((t, i) => (
                  <li key={`${t.toStatus}-${t.createdAt}-${i}`} className="tl-step">
                    <span className="tl-node" aria-hidden="true" />
                    <div>
                      <strong>{t.toStatusLabel}</strong>
                      <div className="muted" style={{ fontSize: 11.5 }}>
                        {day(t.createdAt)} {clock(t.createdAt)}
                        {t.actorName ? ` · ${t.actorName}` : ' · pemohon'}
                      </div>
                      {t.note && <p className="tl-note">{t.note}</p>}
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            {canHandle && detail.allowedTransitions.length > 0 && (
              <section className="card" style={{ padding: '0.9rem 1rem' }}>
                <span className="label">Tindakan</span>

                <label className="field" style={{ marginTop: '0.6rem' }}>
                  <span className="label">Catatan penugasan</span>
                  <input
                    value={assignmentNote}
                    onChange={(e) => setAssignmentNote(e.target.value)}
                    placeholder="Kendaraan, sopir, tim, atau jadwal"
                  />
                </label>

                <label className="field" style={{ marginTop: '0.6rem' }}>
                  <span className="label">Catatan untuk riwayat</span>
                  <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Opsional"
                  />
                </label>

                {canReject && (
                  <label className="field" style={{ marginTop: '0.6rem' }}>
                    <span className="label">Alasan penolakan</span>
                    <input
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Wajib kalau permintaan ditolak"
                    />
                  </label>
                )}

                <div className="row" style={{ marginTop: '0.8rem' }}>
                  {detail.allowedTransitions.map((status) => (
                    <button
                      key={status}
                      type="button"
                      className={status === 'rejected' || status === 'cancelled' ? 'ghost' : 'primary'}
                      disabled={busy !== null}
                      onClick={() => move(status)}
                    >
                      {busy === status
                        ? 'Menyimpan…'
                        : serviceStatusLabel(detail.category, status)}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {canHandle && detail.allowedTransitions.length === 0 && (
              <div className="msg">
                Permintaan ini sudah selesai. Riwayatnya tidak bisa diubah — koreksi
                dilakukan dengan permintaan baru.
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
